// Localization is based on '@ngx-translate/core';
// Please be familiar with official documentations first => https://github.com/ngx-translate/core

import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export interface Locale {
  lang: string;
  data: any;
}

const LOCALIZATION_LOCAL_STORAGE_KEY = 'language';
const UI_LANGS = ['en', 'nl'] as const;

export { LOCALIZATION_LOCAL_STORAGE_KEY };

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private extrasByLang: Record<string, Record<string, unknown>> = {};
  private fetched = new Set<string>();
  private fetching = new Set<string>();
  private applyingExtras = false;
  private langReadyNotified = new Set<string>();

  constructor(private translate: TranslateService) {
    this.translate.addLangs([...UI_LANGS]);
    this.translate.setDefaultLang('en');
  }

  loadTranslations(...args: Locale[]): void {
    args.forEach((locale) => {
      const lang = this.normalizeLang(locale.lang);
      this.extrasByLang[lang] = this.deepMerge(this.extrasByLang[lang] || {}, locale.data || {});
      // Incremental API/menu strings: merge immediately if JSON is already in the store.
      if (this.fetched.has(lang)) {
        this.applyExtras(lang);
      }
    });

    this.translate.addLangs(Object.keys(this.extrasByLang));
    UI_LANGS.forEach((lang) => this.ensureHttpTranslations(lang));
    this.activate(this.getSelectedLanguage());
  }

  setLanguage(lang: string) {
    if (!lang) {
      return;
    }
    const normalized = this.normalizeLang(lang);
    localStorage.setItem(LOCALIZATION_LOCAL_STORAGE_KEY, normalized);
    this.ensureHttpTranslations(normalized);
    this.activate(normalized);
  }

  /**
   * Returns selected language
   */
  getSelectedLanguage(): string {
    const raw =
      localStorage.getItem(LOCALIZATION_LOCAL_STORAGE_KEY) ||
      localStorage.getItem('selectedLanguage') ||
      this.translate.getDefaultLang();
    // Normalize API-style codes (nl_nl) and legacy variants to UI codes (nl).
    return this.normalizeLang(raw);
  }

  instant(key: string | Array<string>, interpolateParams?: Object): string | any {
    return this.translate.instant(key, interpolateParams);
  }

  get(key: string | Array<string>, interpolateParams?: Object) {
    return this.translate.get(key, interpolateParams);
  }

  private normalizeLang(raw: unknown): string {
    const lang = String(raw || 'en').toLowerCase().split(/[_-]/)[0] || 'en';
    return UI_LANGS.includes(lang as (typeof UI_LANGS)[number]) ? lang : 'en';
  }

  /**
   * Vocab stubs used to be registered before translate.use(). ngx-translate then
   * treats that language as already loaded and never fetches assets/i18n/*.json.
   * Always pull the JSON file once, then layer extras on top.
   */
  private ensureHttpTranslations(lang: string): void {
    if (this.fetched.has(lang) || this.fetching.has(lang)) {
      return;
    }
    this.fetching.add(lang);

    const applyLoaded = () => {
      this.fetching.delete(lang);
      this.fetched.add(lang);
      this.applyExtras(lang);
      if (lang === this.getSelectedLanguage()) {
        this.activate(lang);
      }
    };

    // Drop any stub so getTranslation() actually hits the HTTP loader.
    this.translate.resetLang(lang);
    this.translate.getTranslation(lang).subscribe({
      next: applyLoaded,
      error: () => {
        this.fetching.delete(lang);
        this.applyExtras(lang);
        if (lang === this.getSelectedLanguage()) {
          this.activate(lang);
        }
      },
    });
  }

  private applyExtras(lang: string): void {
    const extra = this.extrasByLang[lang];
    if (!extra || this.applyingExtras) {
      return;
    }
    this.applyingExtras = true;
    try {
      this.translate.setTranslation(lang, extra, true);
    } finally {
      this.applyingExtras = false;
    }
  }

  private activate(lang: string): void {
    if (!this.fetched.has(lang)) {
      return;
    }
    this.applyExtras(lang);
    const firstReady = !this.langReadyNotified.has(lang);
    if (firstReady) {
      this.langReadyNotified.add(lang);
      // JSON often arrives after a stub use(); bounce so instant() snapshots rebuild.
      if (this.translate.currentLang === lang) {
        const other = lang === 'en' ? 'nl' : 'en';
        if (this.fetched.has(other)) {
          this.translate.use(other);
        }
      }
    }
    if (this.translate.currentLang !== lang) {
      this.translate.use(lang);
    }
  }

  private deepMerge(target: Record<string, unknown>, source: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = { ...target };
    Object.keys(source || {}).forEach((key) => {
      const sv = source[key];
      const tv = out[key];
      if (this.isPlainObject(sv) && this.isPlainObject(tv)) {
        out[key] = this.deepMerge(tv, sv);
      } else {
        out[key] = sv;
      }
    });
    return out;
  }

  private isPlainObject(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }
}
